import {
    MeetingJoinInfo,
    Meeting,
    MeetingEvent,
    MeetingStatus,
    MeetingSurvey,
    Patient,
    User,
    UserJoinInformation,
    SubstituteResult,
    ProposedTimeSlot,
    UserType,
    CalendarSlot,
    ConflictTimeslot,
    MeetingUrlInformation,
    Timeslot,
    MeetingConfirmationInfo,
    CanSubstituteData,
    JoinCodeStatus
} from "../types";

import { dataToPatient } from "./patient-service";
import { CustomAuthoriserData, getUrl, createCustomToken, batchFetchItems } from "./service-utils";

import { dataToUser } from "./users-service";
import { Message } from "../store";
import { splitTimeSlots } from "../types-helpers";
import { PutMeetingValues, MeetingUpdateValues, UserUpdateValues } from "../../../input-types";

const getMeetingURL = (
    clientId: string | undefined,
    parameters?: string | undefined,
) => {

    return getUrl("meeting", clientId, parameters)
}

const dataToProposedTimes = (
    proposedTimes: any,
    split?: boolean | undefined
): ProposedTimeSlot[] | undefined => {
    if (!proposedTimes || !Array.isArray(proposedTimes)) {
        return undefined
    }

    const times = proposedTimes.map((item: any): ProposedTimeSlot => {
        return {
            creatorId: item?.creatorId || item?.userId || "",
            startTime: item?.startTime ? new Date(item.startTime) : new Date(),
            endTime: item?.endTime ? new Date(item.endTime) : new Date(),
        }
    })

    if (!split) {
        return times
    }

    return splitTimeSlots(times, 15, 30)
}

const dataToCalendarSlot = (data: any): CalendarSlot | null => {
    if (!data) {
        return null;
    }

    if (!data.meetingId) {
        console.warn("Tried to convert object without id", data)
        return null
    }

    const meeting: CalendarSlot = {
        meetingId: data.meetingId,
        creatorId: data.creatorId || "",
        startTime: data.startTime ? new Date(data.startTime) : new Date(),
        endTime: data.endTime ? new Date(data.endTime) : new Date(),
        attendeeIds: data.attendeesId || data.attendeeIds || [],
        patientId: data.patientId || "",
        meetingType: data.meetingType || MeetingStatus.Cancelled,
    }

    return meeting;
}

const dataToMeeting = (data: any): Meeting | null => {
    if (!data) {
        return null;
    }

    if (!data.meetingId) {
        console.warn("Tried to convert object without id", data)
        return null
    }

    let realStartTime = undefined

    if (data?.realStartTime) {
        realStartTime = new Date(data.realStartTime);
    }

    let realEndTime = undefined

    if (data?.realEndTime) {
        realEndTime = new Date(data.realEndTime);
    }

    const proposedTimes = dataToProposedTimes(data.proposedTimes, data)

    const meeting: Meeting = {
        meetingId: data.meetingId,
        creatorId: data.creatorId || "",
        created: data.created ? new Date(data.created) : undefined,
        realStartTime,
        realEndTime,
        startTime: data.startTime ? new Date(data.startTime) : new Date(),
        endTime: data.endTime ? new Date(data.endTime) : new Date(),
        attendeeIds: data.attendeesId || data.attendeeIds || [],
        joinedInfo: data.joinedInfo || [],
        organisationIds: data.organisationIds || [],
        patientId: data.patientId || "",
        meetingType: data.meetingType || MeetingStatus.Cancelled,
        survey: data.survey || null,
        proposedTimes,
        clientIds: data.clientIds,
    }

    return meeting;
}

const dataToConflictTimeslot = (data: any): ConflictTimeslot | null => {
    if (!data) {
        return null;
    }

    const conflict: ConflictTimeslot = {
        startTime: data.startTime ? new Date(data.startTime) : new Date(),
        endTime: data.endTime ? new Date(data.endTime) : new Date(),
        conflictingUsers: data.conflictingUsers || [],
        patientConflict: data.patientConflict,
    };

    return conflict;
};

const dataToJoinInfo = (data: any): MeetingJoinInfo | null => {
    if (!data) {
        console.warn("~~Join data (single) was null", data)
        return null
    }

    return {
        userId: data?.userId || "",
        inMeeting: data?.inMeeting || false,
        acceptedInvite: Boolean(data?.acceptedInvite),
        joinTime: data?.joinTime ? new Date(data?.joinTime) : undefined,
        fullName: data?.fullName || "",
        jobTitle: data?.jobTitle || "",
        joinNumber: Number.isInteger(data?.joinNumber) ? data?.joinNumber : -1
    }
}

const dataToSurvey = (data: any): MeetingSurvey | null => {

    const userId = data?.userId

    if (!userId) {
        return null
    }

    return {
        userId,
        responses: data?.responses || []
    }
}

const dataToMeetingEvents = (data: any): {
    meetingId: string,
    events: MeetingEvent[]
} => {

    const meetingId = data?.meetingId

    const events: MeetingEvent[] = data?.events?.map((item: any) => {
        return {
            type: item?.type || "",
            eventDate: item?.eventDate ? new Date(item.eventDate) : new Date(),
            userId: item?.userId || undefined,
            id: item?.id || undefined,
            dateField: item?.dateField ? new Date(item.dateField) : undefined,
            joinType: item?.joinType,
        }
    })

    return {
        meetingId,
        events,
    }
}

/**
 * Fetches a single meeting by their ID
 * @param authToken - Bearer token for authentication
 * @param meetingId - The id of the meeting
 * @param clientId - The id for the client partition
 * @returns Promise resolving to Meeting object or null if not found
 */
const getMeeting = async (
    authToken: string,
    meetingId: string,
    clientId?: string | undefined,
): Promise<Meeting | null> => {

    if (meetingId.length == 0) {
        return null;
    }

    const url = getMeetingURL(clientId, meetingId)

    let response = null;
    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

    } catch (error: unknown) {
        console.log(error, meetingId);
        return null;
    }

    if (response.status === 404) {
        console.warn("Meeting not found", meetingId);
        return null;
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    const data = await response.json();

    const meetings: Meeting[] = data.map(dataToMeeting).filter((item: Meeting | null) => item != null);

    if (meetings.length == 0) {
        console.error("Meeting data was missing")
        return null
    }

    return meetings[0]
};

/**
 * Cancels a given meeting
 * @param authToken - Bearer token for authentication
 * @param meeting - The meeting object to delete
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a string message or an Error
 */
const cancelMeeting = async (
    authToken: string,
    meetingId: string,
    clientId?: string | undefined
): Promise<{
    meeting: Meeting | null,
    patient: Patient | null
}> => {

    const url = getMeetingURL(clientId, meetingId)

    try {
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json()
        const patient = dataToPatient(data?.patient)
        const meeting = dataToMeeting(data?.meeting)

        return {
            patient,
            meeting,
        }

    } catch (error) {
        console.log(error);
        throw error as Error;
    }
};

/**
 * Lists meetings within an optional date range
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @param startRange - Optional start date to filter meetings
 * @param endRange - Optional end date to filter meetings
 * @returns Promise resolving to an array of Meeting objects
 */
const listMeetings = async (
    authToken: string,
    clientId?: string | undefined,
    startRange?: Date,
    endRange?: Date,
): Promise<CalendarSlot[]> => {

    if (clientId != undefined && clientId.length == 0) {
        return []
    }

    let url = getMeetingURL(clientId)

    if (startRange) {
        url.searchParams.append("startRange", startRange.toISOString())
    }

    if (endRange) {
        url.searchParams.append("endRange", endRange.toISOString())
    }

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        if (!Array.isArray(data)) {
            return []
        }

        return data.map(dataToCalendarSlot).filter(item => item != null)
    } catch (error) {
        console.log(error);
        return [];
    }
};

/**
 * Gets meeting data for conflict checking based on userIds and optionally patientId
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @param startRange - Start date to filter meetings
 * @param endRange - End date to filter meetings
 * @param userIds - Users to get schedule data for
 * @param patient - Optional patient to get schedule data for
 * @returns Promise resolving to an array of Meeting objects
 */
const listScheduleData = async (
    authToken: string,
    startRange: Date,
    endRange: Date,
    userIds: string[],
    patientId?: string | undefined,
    clientId?: string | undefined,
): Promise<CalendarSlot[]> => {

    if (clientId != undefined && clientId.length == 0) {
        return []
    }

    let url = getMeetingURL(clientId)

    url.searchParams.append("startRange", startRange.toISOString())
    url.searchParams.append("endRange", endRange.toISOString())
    url.searchParams.append("patientId", userIds.join(","))

    if (patientId) {
        url.searchParams.append("patientId", patientId)
    }

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        if (!Array.isArray(data)) {
            return []
        }

        return data.map(dataToCalendarSlot).filter(item => item != null)
    } catch (error) {
        console.log(error);
        return [];
    }
};

/**
 * Adds a new meeting
 * @param authToken - Bearer token for authentication 
 * @param meeting - The meeting object to add
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a string message or an Error
 */
const addMeeting = async (
    authToken: string,
    meeting: PutMeetingValues,
    clientId?: string,
): Promise<{
    meeting: Meeting | null,
    patient: Patient | null
}> => {

    const endTime = new Date(meeting.startTime.getTime() + 30 * 60 * 1000)

    const url = getMeetingURL(clientId)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...meeting,
                endTime,
            }),
        });

        if (!response.ok) {
            if (response.status === 409) {
                // Handle the conflict error specifically
                return response.json().then((data) => {
                    throw new Error(data.message);
                });
            } else {
                throw new Error("Network response was not ok");
            }
        }

        const data = await response.json()

        const createdMeeting = dataToMeeting(data?.meeting)
        const patient = dataToPatient(data?.patient)

        return {
            meeting: createdMeeting,
            patient,
        }
    } catch (error) {
        console.log(error);
        throw error as Error;
    }
};

/** 
 * Updates an existing meeting
 * @param authToken - Bearer token for authentication
 * @param values - The meeting object with updated values
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a string message or null if not found
*/
const updateMeeting = async (
    authToken: string,
    meetingId: string,
    values: MeetingUpdateValues,
    clientId?: string | undefined,
): Promise<{
    meeting: Meeting | null,
    patient: Patient | null
}> => {
    const url = getMeetingURL(clientId, meetingId)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(values),
        });

        if (!response.ok) {
            if (response.status === 409) {
                // Handle the conflict error specifically
                return response.json().then((data) => {
                    throw new Error(data.message);
                });
            } else {
                throw new Error(response.status + " Network response was not ok");
            }
        }

        const data = await response.json()

        const meeting = dataToMeeting(data?.meeting)
        const patient = dataToPatient(data?.patient)

        return {
            meeting,
            patient
        }
    } catch (error) {
        console.error("Failed to update meeting", error);
        throw error as Error;
    }
}

/** 
 * Adds a new training meeting
 * @param authToken - Bearer token for authentication
 * @param userId - The id of the user 
 * @param clientId - The id for the client partition
 * @returns Promise resolving to Meeting object or null
 */
const addTrainingMeeting = async (
    authToken: string,
    clientId?: string | undefined,
): Promise<Meeting | null> => {
    const url = getMeetingURL(clientId)

    url.searchParams.append("training", "true")

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(response.status + " Network response was not ok");
        }

        const data = await response.json()

        const meeting = dataToMeeting(data?.meeting)

        return meeting
    } catch (error) {
        console.error("Failed to update meeting", error);
        throw error as Error;
    }
}

/** 
 * Fetches multiple meetings by their IDs
 * @param authToken - Bearer token for authentication
 * @param meetingIds - Array of meeting IDs to fetch
 * @param showDeleted - Whether to include deleted meetings
 * @param clientId - The id for the client partition
 * @returns Promise resolving to an array of Meeting objects or nulls
 */
const getMeetingsWithIds = async (
    authToken: string,
    meetingIds: string[],
    clientId?: string | undefined,
): Promise<(Meeting | null)[]> => {

    return batchFetchItems(
        authToken,
        meetingIds,
        getMeetingURL,
        dataToMeeting,
        clientId,
    )
}

/**
 * Updates the survey for a meeting
 * @param authToken - Bearer token for authentication
 * @param meetingId - The id of the meeting
 * @param survey - The survey object to update
 * @param clientId - The id for the client partition
 * @returns Promise resolving to a string message or an Error
 */
const updateMeetingSurvey = async (
    authData: CustomAuthoriserData,
    survey: MeetingSurvey,
): Promise<MeetingSurvey | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Update meeting attendance info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/survey`)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                meetingId: authData.meetingId,
                survey,
            }),
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json()

        return dataToSurvey(data?.survey)
    } catch (error) {
        console.error("Failed to update survey", error);
        throw error as Error;
    }
}

/** 
 * Exports meeting survey data within an optional date range
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @param startRange - Optional start date to filter surveys
 * @param endRange - Optional end date to filter surveys
 * @returns Promise resolving to exported survey data as a string
 */
const exportMeetingSurvey = async (
    authToken: string,
    clientId?: string | undefined,
    startRange?: Date,
    endRange?: Date,
): Promise<string> => {

    let finalUrl = getMeetingURL(clientId, `meetingId/survey`)

    if (startRange) {
        finalUrl.searchParams.append(`startRange`, startRange.toISOString())
    }

    if (endRange) {
        finalUrl.searchParams.append(`endRange`, endRange.toISOString())
    }

    try {
        const response = await fetch(finalUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        return data

    } catch (error) {
        console.log(error);
        throw error as Error
    }
};

/**
 * Fetches meeting events by their ID
 * @param authToken - Bearer token for authentication
 * @param meetingId - The id of the meeting
 * @param clientId - The id for the client partition
 * @returns Promise resolving to Meeting object or null if not found
 */
const getMeetingEvents = async (
    authToken: string,
    meetingIds: string[],
    clientId?: string | undefined,
): Promise<{
    meetingId: string,
    events: MeetingEvent[]
}[]> => {

    return batchFetchItems(
        authToken,
        meetingIds,
        getMeetingURL,
        dataToMeetingEvents,
        clientId,
        `${meetingIds.join(",")}/events`
    )
};

/**
 * Updates the attendance status for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @param accept - Boolean indicating whether the user accepts the meeting
 * @returns Promise resolving to true if the update was successful, false otherwise
 */
const updateMeetingAttendance = async (
    authData: CustomAuthoriserData,
    accept: boolean,
): Promise<{
    accept: boolean
} | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Update meeting attendance info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/confirm`)

    const body = {
        confirmed: accept,
    }

    let response = null;
    try {
        response = await fetch(url, {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.log(error, authData.meetingId);
        return null
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    return {
        accept
    }
}

/**
 * Fetches the attendance status for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @returns Promise resolving to MeetingConfirmationInfo object or null if not found
 */
const getMeetingAttendance = async (
    authData: CustomAuthoriserData,
): Promise<MeetingConfirmationInfo | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting Attendance info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/confirm`)

    let response = null;
    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.log(error, authData.meetingId);
        return null;
    }

    const data = await response.json();

    if (!response.ok) {
        console.error("Network response was not ok")
        return {
            meetingType: data?.meetingType || MeetingStatus.Abandoned,
            startTime: data?.startTime ? new Date(data?.startTime) : new Date(),
            endTime: data?.endTime ? new Date(data?.endTime) : new Date(),
            acceptedInvite: data?.acceptedInvite,
            message: data?.message || "Failed to check attendance",
        }
    }

    let message = data?.message

    if (!message) {
        if (data?.meetingType) {
            message = data?.meetingType === MeetingStatus.Scheduled ?
                "success" : "Meeting status is " + data?.meetingType
        } else {
            message = "Failed to check attendance"
        }
    }

    return {
        meetingType: data?.meetingType || MeetingStatus.Scheduled,
        startTime: data?.startTime ? new Date(data?.startTime) : new Date(),
        endTime: data?.endTime ? new Date(data?.endTime) : new Date(),
        acceptedInvite: data?.acceptedInvite,
        message,
    }
}

/**
 * Fetches the join information for a meeting attendee
 * @param authData - Custom authoriser data containing meeting and client information
 * @returns Promise resolving to UserJoinInformation object or null if not found
 */
const getMeetingJoinInfo = async (
    authData: CustomAuthoriserData,
): Promise<UserJoinInformation | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/${authData.userId}`)

    let response = null;
    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.error(error);
        return null;
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    const data = await response.json();

    let items: MeetingJoinInfo[] | null = null

    if (data?.joinInfo) {
        items = data.joinInfo.map(dataToJoinInfo)
    } else {
        items = data.map(dataToJoinInfo)
    }

    const survey = dataToSurvey(data?.survey) || undefined

    let joinNumber = -1
    if (data?.joinNumber != undefined && Number.isInteger(data.joinNumber)) {
        joinNumber = data.joinNumber
    }

    return {
        fullName: data?.fullName,
        jobTitle: data?.jobTitle,
        userType: data?.userType,
        joinNumber,
        joinInfo: items || [],
        meetingViews: data?.meetingViews || [],
        survey,
    }
}

/**
 * Fetches the join information for a meeting attendee
 * @param authData - Custom authoriser data containing meeting and client information
 * @returns Promise resolving to download urls for photos models and the patient display id
 */
const getMeetingUrls = async (
    authData: CustomAuthoriserData,
): Promise<MeetingUrlInformation | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/url`)

    let response = null;
    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.error(error);
        return null;
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    const data = await response.json();

    return {
        patientDisplayId: data?.patientDisplayId,
        photoUrls: data?.photoUrls || [],
        modelUrls: data?.modelUrls || [],
    }
}

/**
 * Updates the information for a meeting attendee
 * @param authData - Custom authoriser data containing meeting and client information
 * @param user - User object containing updated attendee information
 * @returns Promise resolving to the updated User object or null if the update failed
 */
const updateMeetingAttendee = async (
    authData: CustomAuthoriserData,
    user: UserUpdateValues
): Promise<User | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/${authData.userId}`)

    let response = null;
    try {
        response = await fetch(url, {
            method: "PUT",
            body: JSON.stringify(user),
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.error(error);
        return null;
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    const data = await response.json();

    return dataToUser(data)
}

/**
 * Adds substitute attendees to a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @param emails - Array of email addresses for the substitute attendees
 * @returns Promise resolving to an object containing a message and the result of the substitution
 */
const addSubstituteAttendee = async (
    authData: CustomAuthoriserData,
    emails: string[]
): Promise<{
    message: string,
    substituteResult: SubstituteResult[]
}> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return {
            message: "Missing token!",
            substituteResult: []
        }
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/attendees`)

    const body = {
        meetingId: authData.meetingId,
        emails,
    }

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        const data = await response.json()

        const message = data?.message

        const substituteResult: SubstituteResult[] = data?.substituteResult.map(
            (item: any): SubstituteResult => {
                return {
                    userId: item?.userId || "",
                    userType: item?.userType || UserType.Inactive,
                    email: item?.email || "",
                    status: item?.status || 500,
                    message: item?.message || "Unknown Error"
                }
            }
        )

        if (!response.ok) {
            throw new Error(message)
        }

        return {
            message,
            substituteResult,
        }
    } catch (error) {
        console.error("Failed to add substitute", error);
        throw error as Error;
    }
}

/**
 * Requests a substitute link for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @returns Promise resolving to a message indicating the result of the request
 */
const requestSubstituteLink = async (
    authData: CustomAuthoriserData,
): Promise<Message> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return {
            message: "Missing token!",
            type: "error",
        }
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/attendees`)

    const body = {
        meetingId: authData.meetingId,
    }

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        const data = await response.json()

        const message = data?.message

        if (!response.ok) {
            throw new Error(message)
        }

        return {
            message,
            type: response.ok ? "success" : "error"
        }
    } catch (error) {
        console.error("Failed to add substitute", error);
        throw error as Error;
    }
}

/**
 * Checks if a substitute attendee can be added to a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @returns Promise resolving to a message indicating whether substitution is possible
 */
const canSubstituteAttendee = async (
    authData: CustomAuthoriserData,
): Promise<CanSubstituteData> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return {
            message: "Invalid URL",
            meetingType: MeetingStatus.Abandoned,
            joinCodeStatus: JoinCodeStatus.cannotJoinMeeting,
        }
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/attendees`)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json()

        return {
            message: data?.message || "Unknown message",
            meetingType: data?.meetingType || MeetingStatus.Abandoned,
            domainWhiteList: data?.domainWhiteList || [],
            joinCodeStatus: data?.joinCodeStatus || JoinCodeStatus.cannotJoinMeeting
        }
    } catch (error) {
        console.error("Failed to check substitute", error);
        throw error as Error;
    }
}

/**
 * Updates the proposed time slots for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @param proposedTimes - Array of proposed time slots to be updated
 * @returns Promise resolving to an object containing a message and the updated proposed time slots
 */
const updateProposedTimes = async (
    authData: CustomAuthoriserData,
    proposedTimes: Timeslot[],
): Promise<{
    message: string,
    result: ProposedTimeSlot[] | undefined
}> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return {
            message: "Missing token!",
            result: undefined,
        }
    }

    if (proposedTimes.length == 0) {
        return {
            message: "Missing variables!",
            result: undefined,
        }
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/proposed`)

    const body = {
        meetingId: authData.meetingId,
        proposedTimes,
    }

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        const data = await response.json()

        const message = data?.message
        const proposedTimes = dataToProposedTimes(data, undefined)

        if (!response.ok) {
            throw new Error(message)
        }

        return {
            message,
            result: proposedTimes,
        }
    } catch (error) {
        console.error("Failed to propose times", error);
        throw error as Error;
    }
}

/**
 * Requests new proposed time slots for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @param targetId - ID of the target attendee for whom new proposed times are requested
 * @returns Promise resolving to an object containing a message indicating the result of the request
 */
const requestNewProposedTimes = async (
    authData: CustomAuthoriserData,
    targetId: string,
): Promise<Message> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return {
            message: "Missing token!",
            type: "error",
        }
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/proposed`)

    url.searchParams.append("targetId", targetId)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json()

        const message = data?.message

        if (!response.ok) {
            throw new Error(message)
        }

        return {
            message,
            type: "success",
        }
    } catch (error) {
        console.error("Failed to propose times", error);
        throw error as Error;
    }
}

/**
 * Confirms the proposed meeting times for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @param startTime - The confirmed start time of the meeting
 * @param endTime - The confirmed end time of the meeting
 * @returns Promise resolving to an object containing a message and the confirmed start and end times
 */
const confirmMeetingTimes = async (
    authData: CustomAuthoriserData,
    startTime: Date,
    endTime: Date,
): Promise<{
    message: string,
    startTime: Date | null,
    endTime: Date | null,
}> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return {
            message: "Missing token!",
            startTime: null,
            endTime: null,
        }
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/proposed`)

    const body = {
        meetingId: authData.meetingId,
        startTime,
        endTime,
    }

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        const data = await response.json()

        const message = data?.message

        const startTime = data?.startTime ? new Date(data.startTime) : null
        const endTime = data?.endTime ? new Date(data.endTime) : null

        if (!response.ok) {
            throw new Error(message)
        }

        return {
            message,
            startTime,
            endTime,
        }
    } catch (error) {
        console.error("Failed to propose times", error);
        throw error as Error;
    }
}

/**
 * Retrieves the proposed meeting times for a meeting
 * @param authData - Custom authoriser data containing meeting and client information
 * @returns Promise resolving to an object containing the meeting and proposed time slots, or null if retrieval fails
 */
const getProposedMeetingTimes = async (
    authData: CustomAuthoriserData,
): Promise<{
    meeting: CalendarSlot,
    proposedTimes: ConflictTimeslot[],
} | null> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting Attendance info incomplete parameters")
        return null
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/proposed`)

    let response = null;
    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.log(error, authData.meetingId);
        return null;
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    const data = await response.json();

    const meeting = dataToCalendarSlot(data.meeting)

    if (!meeting) {
        return null
    }

    const slots: ConflictTimeslot[] = data?.timeSlots?.map(dataToConflictTimeslot) || []

    const proposedTimes = splitTimeSlots(slots, 15, 30)

    proposedTimes.sort((a, b) => {
        const startDiff = a.startTime.getTime() - b.startTime.getTime()
        if (startDiff !== 0) return startDiff

        // Optional tie-breaker when start times are equal
        return a.endTime.getTime() - b.endTime.getTime()
    })

    return {
        meeting,
        proposedTimes,
    }
}

/**
 * Retrieves the schedule data for a meeting within a specified date range
 * @param authData - Custom authoriser data containing meeting and client information
 * @param startRange - The start date of the range for which to retrieve schedule data
 * @param endRange - The end date of the range for which to retrieve schedule data
 * @returns Promise resolving to an array of calendar slots within the specified date range
 */
const getMeetingScheduleData = async (
    authData: CustomAuthoriserData,
    startRange: Date,
    endRange: Date,
): Promise<CalendarSlot[]> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.log("~~Get Meeting Attendance info incomplete parameters")
        return []
    }

    const url = getMeetingURL(authData.clientId, `${authData.meetingId}/schedule`)

    url.searchParams.append("startRange", startRange.toISOString())
    url.searchParams.append("endRange", endRange.toISOString())

    let response = null;
    try {
        response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    } catch (error: unknown) {
        console.log(error, authData.meetingId);
        return []
    }

    if (!response.ok) {
        console.error("Network response was not ok")
        throw new Error("Network response was not ok");
    }

    const data = await response.json();

    if (!data || !Array.isArray(data)) {
        return []
    }

    return data.map(dataToCalendarSlot).filter(item => item != null)
}

const MeetingsAPIService = {
    getMeeting,
    listMeetings,
    listScheduleData,
    addMeeting,
    cancelMeeting,
    updateMeeting,
    getMeetingsWithIds,
    addTrainingMeeting,
    updateMeetingSurvey,
    exportMeetingSurvey,
    getMeetingEvents,
    updateMeetingAttendance,
    getMeetingAttendance,
    getMeetingJoinInfo,
    updateMeetingAttendee,
    addSubstituteAttendee,
    canSubstituteAttendee,
    requestSubstituteLink,
    updateProposedTimes,
    requestNewProposedTimes,
    confirmMeetingTimes,
    getProposedMeetingTimes,
    getMeetingScheduleData,
    getMeetingUrls,
};

export default MeetingsAPIService;
